// Product filter/attribute keys, shared across the app.
// Kept as CommonJS `.js` (NOT `.ts`) on purpose: this module is the single source
// of truth consumed across two runtimes — the app build and the cron/*.mjs scripts
// (run by plain Node, which cannot import .ts). Keeping it plain JS lets both import
// the same definition instead of mirroring it.
//
// The v2 tenant carries TWO parallel attribute taxonomies on every product:
//   TM_*     — the generic ThiemeMeulenhoff fondsbrede set
//   EXAMEN_* — the Examenbundel-specific set
// The storefront must read the EXAMEN_* set. It is the taxonomy the site was
// built on and the one that survived the migration with the production values
// intact — same pattern as the "Examenbundel"-tagged images and the "EX"
// description language.
//
// Measured side by side on the v2 acceptance tenant (18-08-2026), the difference
// is not cosmetic — TM_* carries different values altogether:
//   EXAMEN_ONDERWIJSTYPE  havo / vwo / vmbo-bbl / vmbo-kbl / vmbo-gl / vmbo-tl
//   TM_ONDERWIJSTYPE      HAVO / VWO / "VMBO G, VMBO T" / HO / Unknown
//   EXAMEN_MERK           Examenbundel / Voordeelpakket / Samengevat / …
//   TM_MERK               Examenbundels / Voordeelpakketten Examenfonds / …
//   EXAMEN_SCHOOLVAK      Duits / Engels / NaSk / …
//   TM_VAK                Duitse taal / Engelse taal / Natuur- en scheikunde I / …
//   EXAMEN_ISBN           populated;  TM_ISBN  absent
// Reading TM_* is what made the acceptance filters differ from production.

// Query-string keys, kept character-for-character identical to production so
// existing/indexed links such as `/producten?list:examen_onderwijstype=havo`
// keep working after the v2 switch. The `list:`/`text:` prefix is cosmetic —
// `constructTextFilters` strips it and only sends the attribute name.
const productFilters = Object.freeze({
  educationType: 'list:examen_onderwijstype',
  subject: 'text:examen_schoolvak',
  brand: 'text:examen_merk'
})

const productAttributes = Object.freeze({
  educationType: 'EXAMEN_ONDERWIJSTYPE',
  subject: 'EXAMEN_SCHOOLVAK',
  brand: 'EXAMEN_MERK',
  isbn: 'EXAMEN_ISBN',
  // Inkijkexemplaar (preview) issuu URL. Holds the same URL as
  // TM_INKIJKEXEMPLAAR; EXAMEN_* is used here for consistency with the rest.
  preview: 'EXAMEN_INKIJKEXEMPLAAR'
})

/**
 * Schoolniveaus the site offers, in display order. These double as the facet
 * values: `EXAMEN_ONDERWIJSTYPE` serves exactly these strings, so a tile can
 * link straight to `?list:examen_onderwijstype=havo`.
 */
const educationLevels = Object.freeze([
  'vmbo-bbl',
  'vmbo-kbl',
  'vmbo-gl',
  'vmbo-tl',
  'havo',
  'vwo'
])

// Keep this a plain shorthand object literal: `cron/sync-algolia-products.mjs`
// does `import { productAttributes } from ...`, and Node only detects named
// exports on a CommonJS module when the export object is statically analysable.
// Wrapping the values in Object.freeze() here breaks that import outright.
module.exports = {
  productFilters,
  educationLevels,
  productAttributes
}
